<template lang="pug">
  #required-questions
    .modal-card
      header.modal-card-head
        WtTitle(size="h3") Your Questions
      section.modal-card-body
        WtTitle(size="h5") Please respond to the following questions to continue to your program.
        WtTile
          .columns.is-centered.is-mobile.is-full-width
            .column.p-0.box.confirmation-message.animated.fadeIn
              .column.px-4.is-two-thirds-desktop
                div(v-for="q in requiredQuestions" :key="q.id" class="my-5")
                  .is-flex.is-justify-content-space-between
                    WtLabel.is-size-5(
                      slot="label"
                      :text="q.question"
                      :required="q.required"
                    )
                    BTag.points-tag(
                      v-if="q.pointValue"
                      rounded
                    ) {{q.pointValue}} pts
                  .is-size-7.has-text-grey.question-description(v-html="q.description")
                  QuestionAnswers(:question="q" @onAnswer="collectAnswer($event)")
      footer.modal-card-foot.custom-modal-card-foot.is-flex.is-justify-content-flex-end
        WtButton.is-family-secondary.register-button(
          v-if="showDismissButton"
          type="button"
          color="red"
          @click.native="dismissModal"
        ) Dismiss
        WtButton.is-family-secondary.register-button(
          type="button"
          :loading="savingQuestions"
          :disabled="savingQuestions || !allRequiredQuestionsAnswered"
          @click.native="answerQuestions"
        ) Submit
</template>

<script>
import WtTile from "@@/WtTile/WtTile";
import QuestionAnswers from "@@/ProgramQuestions/QuestionAnswers";
import VclForm from "@@/VclRegForm/VclRegForm";
import WtLabel from "@@/WtLabel/WtLabel";
import WtButton from "@@/WtButton/WtButton";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import WtTitle from "@@/WtTitle/WtTitle";
import { mapGetters, mapActions } from "vuex";

export default {
  name: "RequiredQuestions",

  components: { WtTitle, WtTile, QuestionAnswers, VclForm, WtLabel, WtButton },

  mixins: [dateUtilityMixin],

  props: {
    showDismissButton: {
      type: Boolean,
      default: false
    },
    dismissModal: {
      type: Function,
      default: () => {}
    }
  },

  data() {
    return {
      formDataQuestions: [],
      savingQuestions: false
    };
  },

  computed: {
    ...mapGetters("members", ["member"]),

    requiredQuestions() {
      return this._.get(this.member, "unansweredQuestions", []).filter(
        q => q.required
      );
    },

    allRequiredQuestionsAnswered() {
      for (const question of this.requiredQuestions) {
        const answeredQuestion = this.formDataQuestions.find(
          q => q.id === question.id
        );
        if (
          !answeredQuestion ||
          (typeof answeredQuestion.answer === "string" &&
            answeredQuestion.answer.replace(/\s+/g, "") === "")
        )
          return false;
      }
      return true;
    }
  },

  methods: {
    ...mapActions("program", ["saveUserAnswers"]),
    ...mapActions("members", ["updateMemberValue", "updateMemberCopy"]),
    ...mapActions(["snackBar"]),

    collectAnswer(event) {
      const questionResult = {
        id: event.questionId,
        answer: this._.isArray(event.answer)
          ? event.answer.join(",")
          : event.answer
      };

      const arrayQuestionResult = this._.find(this.formDataQuestions, {
        id: questionResult.id
      });

      if (arrayQuestionResult) {
        arrayQuestionResult.answer = questionResult.answer;
      } else {
        this.formDataQuestions.push(questionResult);
      }
    },

    answerQuestions: async function() {
      this.savingQuestions = true;
      try {
        await this.saveUserAnswers({
          programId: this.$auth.user().programId,
          memberId: this.$auth.user().id,
          questionAnswers: this.formDataQuestions
        });
        const answeredQuestions = this._.clone(this.member.answeredQuestions);
        const unansweredQuestions = this._.clone(
          this.member.unansweredQuestions
        );
        this.formDataQuestions.forEach(answeredQuestion => {
          // add question to answered questions
          const answerCopy = unansweredQuestions.find(
            q => q.id === answeredQuestion.id
          );
          answerCopy.memberAnswered = true;
          answerCopy.memberAnswer = {
            date: this.today("DATE_TIME_FULL").formatted,
            response: answeredQuestion.answer
          };
          answeredQuestions.push(answerCopy);

          // remove from unanswered questions
          const questionIndex = unansweredQuestions.findIndex(
            q => q.id === answeredQuestion.id
          );
          unansweredQuestions.splice(questionIndex, 1);
        });

        //update vuex store
        this.updateMemberValue({
          key: "answeredQuestions",
          value: answeredQuestions
        });
        this.updateMemberValue({
          key: "unansweredQuestions",
          value: unansweredQuestions
        });

        this.updateMemberCopy({
          key: "answeredQuestions",
          value: answeredQuestions
        });
        this.updateMemberCopy({
          key: "unansweredQuestions",
          value: unansweredQuestions
        });
        this.snackBar({
          duration: 10000,
          queue: false,
          message: "Your answers have been submitted!",
          type: "is-success"
        });
        this.formDataQuestions = [];
        this.savingQuestions = false;
      } catch (e) {
        this.savingQuestions = false;
      }
    }
  }
};
</script>

<style lang="sass" scoped>
#required-questions
  .is-full-width
    width: 100%
  .points-tag
    background-color: $grey
    color: white
    font-family: $family-sans-serif-bold
</style>
